import { useState, useEffect, useRef, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import {
  Bell,
  Search,
  Sun,
  Moon,
  Menu,
  ChevronDown
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { useUIStore } from '@/lib/ui-store';
import { LanguageSwitcher } from './LanguageSwitcher';
import { AccountDropdown } from './AccountDropdown';
import { useTranslation } from 'react-i18next';
import { getSession, Session } from '@/lib/store';

/**
 * TopBar Component (Client Side)
 * Rebuilt for professional Fintech UI
 * Account dropdown: fully functional, i18n, ARIA, keyboard nav.
 */
export function TopBar() {
  const location = useLocation();
  const pathname = location.pathname;
  const [isScrolled, setIsScrolled] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const { theme, setTheme, toggleSidebar } = useUIStore();
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === 'ar';
  const [user, setUser] = useState<Session | null>(null);
  const profileButtonRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    setUser(getSession());
    document.documentElement.classList.toggle('dark', theme === 'dark');

    const handleScroll = () => setIsScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [theme]);

  const toggleTheme = () => {
    setTheme(theme === 'dark' ? 'light' : 'dark');
  };

  const getPageTitle = () => {
    if (pathname?.includes('/genvest')) return t('dashboard.topbar.genvest');
    if (pathname?.includes('/finance')) return t('dashboard.topbar.finance');
    if (pathname?.includes('/companies')) return t('dashboard.topbar.legal');
    if (pathname?.includes('/infrastructure')) return t('dashboard.nav.infrastructure');
    return t('dashboard.topbar.dashboard');
  };

  const handleToggleDropdown = useCallback(() => {
    setIsDropdownOpen(prev => !prev);
  }, []);

  const handleCloseDropdown = useCallback(() => {
    setIsDropdownOpen(false);
  }, []);

  const displayName = user?.metadata?.demo
    ? t('auth.demo.account_name')
    : (user?.name || 'Amine B.');

  const initials = displayName
    .split(' ')
    .map((n: string) => n[0])
    .join('')
    .toUpperCase()
    .substring(0, 2);

  return (
    <div className={cn(
      "flex justify-between items-center transition-all duration-300 min-w-0 w-full px-2 sm:px-4",
      isScrolled && "border-border"
    )}>
      <div className="flex items-center gap-1.5 sm:gap-4 min-w-0 flex-shrink">
        {/* Mobile Menu Toggle */}
        <button
          onClick={toggleSidebar}
          className="lg:hidden w-9 h-9 sm:w-10 sm:h-10 flex-shrink-0 flex items-center justify-center rounded-lg bg-bg-secondary border border-border text-text-primary active:scale-95 transition-all"
        >
          <Menu size={18} className="sm:hidden" />
          <Menu size={20} className="hidden sm:block" />
        </button>

        <div className={cn("flex flex-col min-w-0", pathname === '/dashboard' && "hidden sm:flex")}>
          <h1 className="text-[11px] sm:text-lg font-black ltr:tracking-tight rtl:tracking-normal text-text-primary uppercase leading-tight truncate">
            {getPageTitle()}
          </h1>
          <div className="hidden sm:flex items-center gap-2">
            <div className="w-1 h-1 rounded-full bg-green-500 shadow-[0_0_8px_rgba(34,197,94,0.4)]" />
            <span className="text-[9px] font-bold text-text-muted uppercase ltr:tracking-widest rtl:tracking-normal leading-none">{t('dashboard.topbar.operational')}</span>
          </div>
        </div>
      </div>

      <div className="flex items-center gap-1.5 sm:gap-4 min-w-0">
        {/* Search */}
        <div className="relative group hidden lg:block">
          <Search className={cn(
            "absolute top-1/2 -translate-y-1/2 text-text-muted group-focus-within:text-accent-primary transition-colors",
            isRTL ? "right-3.5" : "left-3.5"
          )} size={16} />
          <input
            type="text"
            placeholder={t('dashboard.topbar.search_placeholder')}
            className={cn(
              "bg-bg-secondary/50 border border-border text-sm font-medium placeholder:text-text-muted focus:border-accent-primary/50 focus:bg-bg-tertiary focus:ring-1 focus:ring-accent-primary/20 outline-none py-2 rounded-xl w-64 xl:w-80 transition-all font-sans",
              isRTL ? "pr-10 pl-4" : "pl-10 pr-4"
            )}
          />
        </div>

        {/* Actions */}
        <div className="flex items-center gap-1 sm:gap-2 border-r border-border pr-2 sm:pr-4 mr-1 sm:mr-2 flex-shrink-0">
          <button className="w-9 h-9 flex items-center justify-center rounded-lg hover:bg-bg-secondary text-text-secondary hover:text-text-primary transition-all relative shrink-0">
            <Bell size={18} />
            <span className="absolute top-2 right-2 w-1.5 h-1.5 bg-accent-primary rounded-full border-2 border-bg-primary" />
          </button>

          <button
            onClick={toggleTheme}
            className="hidden sm:flex w-8 h-8 sm:w-9 sm:h-9 items-center justify-center rounded-lg hover:bg-bg-secondary text-text-secondary hover:text-text-primary transition-all"
            title={theme === 'dark' ? t('dashboard.topbar.light_mode') : t('dashboard.topbar.dark_mode')}
          >
            {theme === 'dark' ? <Sun size={18} /> : <Moon size={18} />}
          </button>

          <LanguageSwitcher />
        </div>

        {/* User Profile — account dropdown trigger */}
        <div className="relative flex-shrink-0">
          <button
            ref={profileButtonRef}
            id="account-menu-button"
            aria-haspopup="true"
            aria-expanded={isDropdownOpen}
            aria-controls="account-menu"
            onClick={handleToggleDropdown}
            className={cn(
              "flex items-center gap-1.5 sm:gap-3 pl-1 sm:pl-2 py-1 rounded-lg hover:bg-bg-secondary transition-all group",
              "focus:outline-none focus-visible:ring-2 focus-visible:ring-accent-primary/50 focus-visible:ring-offset-1 focus-visible:ring-offset-bg-primary",
              isDropdownOpen && "bg-bg-secondary"
            )}
          >
            <div className={cn("text-end hidden md:block shrink-0", isRTL && "text-start")}>
              <p className="text-[13px] font-bold text-text-primary leading-none mb-1 group-hover:text-accent-primary transition-colors whitespace-nowrap">
                {displayName}
              </p>
              <p className="text-[10px] font-bold text-text-muted leading-none uppercase ltr:tracking-tighter rtl:tracking-normal whitespace-nowrap">
                {t('auth.profiles.ENTERPRISE')}
              </p>
            </div>
            <div className="relative">
              <div className="w-8 h-8 sm:w-9 sm:h-9 bg-accent-primary rounded-lg flex items-center justify-center shadow-lg shadow-accent-primary/20 text-white font-bold text-sm">
                {initials}
              </div>
              <div className="absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 bg-green-500 rounded-full border-2 border-bg-primary" />
            </div>
            <ChevronDown
              size={12}
              className={cn(
                "text-text-muted group-hover:text-text-primary transition-all duration-200 hidden xs:block",
                isDropdownOpen && "rotate-180 text-accent-primary"
              )}
            />
          </button>

          {/* Dropdown */}
          <AccountDropdown
            isOpen={isDropdownOpen}
            onClose={handleCloseDropdown}
            anchorRef={profileButtonRef}
          />
        </div>
      </div>
    </div>
  );
}
